import { Icon } from "@iconify/react";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { TranslationKey } from "@/locales/i18n";
import { useI18n } from "@/locales/i18n-provider";

const OPTIONS: ReadonlyArray<{ value: "light" | "dark" | "system"; labelKey: TranslationKey; icon: string }> = [
  { value: "light", labelKey: "theme.light", icon: "tabler:sun" },
  { value: "dark", labelKey: "theme.dark", icon: "tabler:moon" },
  { value: "system", labelKey: "theme.system", icon: "tabler:device-desktop" },
] as const;

export type ThemeProps = {
  onThemeChanged?: (theme: "light" | "dark" | "system") => void;
};

/** 主题模式切换（浅色 / 深色 / 跟随系统）。 */
export function ThemeToggle({ onThemeChanged }: ThemeProps) {
  const { theme, setTheme } = useTheme();
  const { t } = useI18n();

  const handleThemeChange = (newTheme: "light" | "dark" | "system") => {
    setTheme(newTheme);
    onThemeChanged?.(newTheme);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button aria-label={t("theme.switch")} className="size-10 rounded-xl" size="icon" variant="ghost">
          <Icon className="dark:hidden size-4" icon="tabler:sun" />
          <Icon className="hidden dark:block size-4" icon="tabler:moon" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={theme} onValueChange={(value) => handleThemeChange(value as "light" | "dark" | "system")}>
          {OPTIONS.map(({ value, labelKey, icon }) => (
            <DropdownMenuRadioItem key={value} value={value}>
              <Icon icon={icon} />
              <span className="text-sm">{t(labelKey)}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
